/* Wechsel zum vorherigen/nächsten Spieler im offenen Profil: Pfeile, Tastatur, Wischen.
 *
 * Die Reihenfolge kommt aus dem Listenkontext, den die Liste beim Öffnen mitgibt (usePlayers.js
 * profileContext, Regeln in domain/profileNav.js). Ohne Kontext ist `position` null und nichts reagiert.
 *
 * Wischen gilt im Inhalt des Profils (`bodyEl`), nur Touch/Stift (mit der Maus gibt es die Pfeile). Die Geste
 * wird wie im Pager (domain/squadPages.js swipeIntent) erst nach kurzer Bewegung entschieden; senkrecht
 * bleibt das normale Scrollen. Sie beginnt nicht in Eingabefeldern, Videos und Elementen mit eigenem
 * waagerechten Scrollen (isSwipeBlocked).
 *
 * Bewegung (Nachtrag 2026-10-13): Das Profil wechselt als EINE Karte. Kopf (Name, Status) und Inhalt (Tabs,
 * Sektoren) teilen sich einen Versatz (`cardStyle`), den PlayerPanel.vue auf beide setzt; fest bleiben die Leiste
 * (Pfeile, "7 / 38") und der Schließen-Knopf. Variante "hinaus, dann herein" — kein zweites Profil daneben: Die
 * Karte enthält Formulare und lädt Daten (Transfers, Fotos), ein Doppel würde Netz und Zustand verdoppeln.
 *   Ziehen          Versatz folgt dem Finger (ohne Übergang), an den Enden nur mit Gegenzug
 *   über Schwelle   aktuelle Karte gleitet hinaus (OUT_MS); AM UMSCHALTPUNKT wechselt der Spieler (Daten, Zähler,
 *                   Nachbarn), die neue Karte startet auf der Gegenseite und gleitet herein (IN_MS)
 *   unter Schwelle  Karte federt zurück (BACK_MS)
 *   Pfeile, Tasten  dieselbe Hinaus-/Herein-Bewegung, nur ohne Ziehen
 * Fest bleiben die Reiterleiste (liegt im Inhalt, aber außerhalb von `.card-slide`), die Leiste mit Pfeilen und
 * Zähler und der Schließen-Knopf.
 * Während der Bewegung sind weitere Wechsel gesperrt.
 *
 * prefers-reduced-motion: reduce (ermittelt über window.matchMedia bei JEDEM Wechsel, nicht zwischengespeichert):
 * kein Gleiten, sondern ein kurzes Überblenden (FADE_MS aus, FADE_MS ein = ca. 120 ms). Der Wert kommt vom
 * Betriebssystem bzw. Browser — unter Windows 11 genügt "Animationseffekte" aus (Einstellungen → Barrierefreiheit →
 * Visuelle Effekte), auch der Energiesparmodus kann ihn auslösen. Ohne dieses Überblenden war der Wechsel dort ein
 * harter Schnitt, der aussah, als würde die Bewegung nicht laufen.
 *
 * Es wird nichts gespeichert und kein Verlaufseintrag angelegt: Das Profil hat keinen eigenen URL-Eintrag,
 * Zurück/Schließen führen unverändert zur Liste.
 */

import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { usePlayers } from './usePlayers.js';
import { signedImageUrl } from '../supabase/storage.js';
import { photoPathFor } from '../domain/playerPublic.js';
import { swipeIntent } from '../domain/squadPages.js';
import {
  isSwipeBlocked, isTypingTarget, swipeStepDelta, dragOffset, resolveStep,
} from '../domain/profileNav.js';

const NOTE_MS = 4000;
/* Dauer der Bewegungen (ms) */
export const OUT_MS = 200;
export const IN_MS = 250;
export const BACK_MS = 200;
/* Überblenden bei reduzierter Bewegung: je Richtung, zusammen ca. 120 ms */
export const FADE_MS = 60;

/**
 * @param {object} o
 * @param {import('vue').Ref} o.bodyEl   Inhalt des Profils (scrollt, nimmt die Geste an)
 * @param {import('vue').Ref} o.nameEl   Name (bekommt nach dem Wechsel den Fokus)
 * @param {() => Promise<boolean>} [o.beforeLeave]  false = bleiben (z. B. ungespeicherte Änderungen)
 * @param {() => boolean} [o.busy]       true = gerade nicht wechseln (Speichern läuft)
 * @param {() => void} [o.onSwitch]      am Umschaltpunkt VOR dem Spielerwechsel gerufen (z. B. Reiter merken)
 */
export function useProfileNav({
  bodyEl, nameEl, beforeLeave = async () => true, busy = () => false, onSwitch = () => {},
}) {
  const {
    profilePosition, canStepProfileBy, openAdjacent, profileContext, openPlayerId, players,
  } = usePlayers();

  const active = computed(() => profilePosition.value !== null);
  const canPrev = computed(() => active.value && canStepProfileBy(-1));
  const canNext = computed(() => active.value && canStepProfileBy(1));
  /* Anzahl übersprungener, nicht ladbarer Spieler beim letzten Wechsel (für den Hinweis im Kopf) */
  const skipped = ref(0);
  let noteTimer = 0;

  /* Versatz der Karte in px (Ziel; die Bewegung macht der CSS-Übergang) und dessen Übergang */
  const offset = ref(0);
  const transition = ref('none');
  /* Nur bei reduzierter Bewegung: Deckkraft 0..1 statt Versatz (null = nicht aktiv) */
  const fade = ref(null);
  const dragging = ref(false);
  /* 'idle' | 'dragging' | 'out' | 'in' | 'back' — out, in, back sperren weitere Wechsel */
  const phase = ref('idle');
  let switching = false; // schon ab der Rückfrage gesperrt, damit nichts doppelt startet
  let disposed = false;
  const timers = new Set();
  const sleep = (ms) => new Promise((resolve) => {
    const id = setTimeout(() => { timers.delete(id); resolve(); }, ms);
    timers.add(id);
  });
  const animating = () => switching || phase.value === 'out' || phase.value === 'in' || phase.value === 'back';

  /* Gemeinsamer Stil für Kopf und Inhalt. null, solange die Karte ruht (kein transform → kein eigener
     Stapelkontext, nichts ändert sich gegenüber vorher). */
  const cardStyle = computed(() => {
    if (fade.value !== null) return { opacity: fade.value, transition: transition.value };
    if (offset.value === 0 && transition.value === 'none') return null;
    return { transform: `translateX(${offset.value}px)`, transition: transition.value };
  });

  const reducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  const widthOf = () => bodyEl.value?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 400);

  function showNote(n) {
    clearTimeout(noteTimer);
    skipped.value = n;
    if (n) noteTimer = setTimeout(() => { skipped.value = 0; }, NOTE_MS);
  }

  /** Karte zurück in die Ruhelage (federn). */
  async function springBack() {
    if (offset.value === 0) {
      transition.value = 'none';
      phase.value = 'idle';
      return;
    }
    const reduced = reducedMotion();
    fade.value = null;
    phase.value = 'back';
    transition.value = reduced ? 'none' : `transform ${BACK_MS}ms ease-out`;
    offset.value = 0;
    if (!reduced) await sleep(BACK_MS);
    if (disposed) return;
    transition.value = 'none';
    phase.value = 'idle';
  }

  /** Spielerwechsel am Umschaltpunkt: Daten, Zähler, Nachbarn; Profil oben, Fokus auf den Namen. */
  async function switchPlayer(delta) {
    onSwitch();
    const result = openAdjacent(delta);
    if (result) showNote(result.skipped);
    await nextTick();
    /* Neues Profil beginnt oben; der Fokus wandert auf den Namen, damit Screenreader den Wechsel mitbekommen */
    if (bodyEl.value) bodyEl.value.scrollTop = 0;
    nameEl.value?.focus?.({ preventScroll: true });
    return result;
  }

  /** Wechsel in Richtung delta (-1/+1). Gibt true zurück, wenn gewechselt wurde. */
  async function step(delta) {
    if (animating()) return false;
    if (!active.value || busy() || !(delta < 0 ? canPrev.value : canNext.value)) return false;
    switching = true;
    try {
      if (!(await beforeLeave())) {
        await springBack();
        return false;
      }
      if (reducedMotion()) {
        /* Kein Gleiten, aber ein kurzes Überblenden: aus (FADE_MS), am Umschaltpunkt wechseln, ein (FADE_MS) */
        offset.value = 0;
        phase.value = 'out';
        transition.value = `opacity ${FADE_MS}ms linear`;
        fade.value = 0;
        await sleep(FADE_MS);
        if (disposed) return false;
        transition.value = 'none';
        const changed = await switchPlayer(delta);
        if (disposed) return false;
        void bodyEl.value?.offsetWidth;
        phase.value = 'in';
        transition.value = `opacity ${FADE_MS}ms linear`;
        fade.value = 1;
        await sleep(FADE_MS);
        if (disposed) return !!changed;
        transition.value = 'none';
        fade.value = null;
        phase.value = 'idle';
        return !!changed;
      }
      const width = widthOf();
      /* hinaus: beim nächsten Spieler nach links, beim vorherigen nach rechts */
      phase.value = 'out';
      dragging.value = false;
      transition.value = `transform ${OUT_MS}ms ease-out`;
      offset.value = delta > 0 ? -width : width;
      await sleep(OUT_MS);
      if (disposed) return false;
      /* Umschaltpunkt: ohne Übergang auf die Gegenseite, dort den neuen Spieler zeigen, dann hereingleiten */
      transition.value = 'none';
      offset.value = delta > 0 ? width : -width;
      const result = await switchPlayer(delta);
      if (disposed) return false;
      if (!result) {
        offset.value = 0;
        phase.value = 'idle';
        return false;
      }
      void bodyEl.value?.offsetWidth; // Layout festschreiben, damit der folgende Übergang von hier startet
      phase.value = 'in';
      transition.value = `transform ${IN_MS}ms ease-out`;
      offset.value = 0;
      await sleep(IN_MS);
      if (disposed) return true;
      transition.value = 'none';
      phase.value = 'idle';
      return true;
    } finally {
      switching = false;
    }
  }

  /* ---------- Nachbarn im Voraus ----------
     Nur das Foto (Fassung md, wie Player Info) des vorherigen und nächsten Spielers. Die Daten haben Liste und
     Speicher schon; Videos, Transfers und Dokumente werden NICHT vorgeladen (Supabase-Egress). Ein Fehler
     bleibt still, das Profil lädt sein Foto dann wie gewohnt beim Öffnen. */
  const preloaded = new Set();
  const neighborIds = computed(() => {
    const ctx = profileContext.value;
    if (!ctx) return [];
    const has = (id) => players.value.some((p) => p.id === id);
    return [-1, 1].map((d) => resolveStep(ctx, openPlayerId.value, d, has)?.id).filter((id) => id != null);
  });
  watch(
    neighborIds,
    (ids) => {
      for (const id of ids) {
        const p = players.value.find((x) => x.id === id);
        const path = p && !p.avatar && !p.bioPhoto ? photoPathFor(p) : null;
        if (!path || preloaded.has(path)) continue;
        preloaded.add(path);
        signedImageUrl(path, 'md')
          .then((url) => {
            if (url && typeof Image !== 'undefined') new Image().src = url;
          })
          .catch(() => preloaded.delete(path));
      }
    },
    { immediate: true }
  );

  /* ---------- Tastatur ---------- */
  function onKeydown(e) {
    if (!active.value || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const t = e.target;
    if (isTypingTarget(t)) return;
    /* Dialoge und Auswahllisten (Vuetify-Overlay) gehören sich selbst */
    if (t?.closest?.('.v-overlay, [role="listbox"], [role="menu"], [role="slider"], [role="tablist"]')) return;
    e.preventDefault();
    step(e.key === 'ArrowRight' ? 1 : -1);
  }

  /* ---------- Wischen ---------- */
  let gesture = null;

  function onPointerdown(e) {
    if (!active.value || e.pointerType === 'mouse' || !e.isPrimary || busy() || animating()) return;
    if (isSwipeBlocked(e.target, bodyEl.value, (el) => getComputedStyle(el))) return;
    gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), intent: null };
  }

  function onPointermove(e) {
    if (!gesture || e.pointerId !== gesture.id) return;
    const dx = e.clientX - gesture.x;
    const dy = e.clientY - gesture.y;
    if (!gesture.intent) {
      gesture.intent = swipeIntent(dx, dy);
      if (gesture.intent === 'horizontal') {
        dragging.value = true;
        phase.value = 'dragging';
        transition.value = 'none';
        try { bodyEl.value?.setPointerCapture?.(e.pointerId); } catch { /* nicht kritisch */ }
      } else if (gesture.intent === 'vertical') {
        gesture = null; // normales Scrollen, nichts anfassen
        return;
      }
    }
    if (gesture?.intent === 'horizontal' && !reducedMotion()) {
      offset.value = dragOffset({ dx, width: widthOf(), canPrev: canPrev.value, canNext: canNext.value });
    }
  }

  /* Nach einem Wisch kein Klick auf das, was unter dem Finger liegt */
  let swallow = false;

  function endGesture(e, cancelled) {
    if (!gesture || e.pointerId !== gesture.id) return;
    const g = gesture;
    gesture = null;
    if (g.intent !== 'horizontal') return;
    dragging.value = false;
    swallow = true;
    setTimeout(() => { swallow = false; }, 0);
    const delta = cancelled
      ? 0
      : swipeStepDelta({
        dx: e.clientX - g.x,
        dt: performance.now() - g.t,
        width: widthOf(),
        canPrev: canPrev.value,
        canNext: canNext.value,
      });
    /* Über der Schwelle: hinaus und herein, vom aktuellen Versatz aus; sonst zurückfedern */
    if (delta) {
      step(delta).then((done) => {
        if (!done && phase.value === 'dragging') springBack(); // nicht gewechselt (z. B. gesperrt): Karte zurück
      });
    } else {
      springBack();
    }
  }

  function onClickCapture(e) {
    if (!swallow) return;
    e.stopPropagation();
    e.preventDefault();
  }

  onMounted(() => window.addEventListener('keydown', onKeydown));
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown);
    clearTimeout(noteTimer);
    disposed = true;
    timers.forEach((id) => clearTimeout(id));
    timers.clear();
  });

  return {
    position: profilePosition, canPrev, canNext, skipped, step, offset, dragging, phase, cardStyle,
    handlers: {
      onPointerdown,
      onPointermove,
      onPointerup: (e) => endGesture(e, false),
      onPointercancel: (e) => endGesture(e, true),
      onClickCapture,
    },
  };
}
